// src/pages/Product.js 
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { Box, Grid, CircularProgress, Alert, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import SizeSelectionDialog from '../components/Product/SizeSelectionDialog';
import SuccessDialog from '../components/Product/SuccessDialog';
import ProductHeader from '../components/ProductPage/ProductHeader';
import ProductImageGallery from '../components/ProductPage/ProductImageGallery';
import ProductDetails from '../components/ProductPage/ProductDetails';

const Product = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [productColors, setProductColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  
  // Discount state - simplified for product-level pricing
  const [discountInfo, setDiscountInfo] = useState({
    hasDiscount: false,
    discountPercent: 0,
    originalPrice: 0,
    discountedPrice: 0
  });

  // Enhanced Messenger click handler
  const handleMessengerClick = () => {
    const pageId = '110699824870937';
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      const messengerAppUrl = `fb-messenger://user-thread/${pageId}`;
      const messengerWebUrl = `https://m.me/${pageId}`;
      
      const appLink = document.createElement('a');
      appLink.href = messengerAppUrl;
      appLink.style.display = 'none';
      document.body.appendChild(appLink);
      
      try {
        appLink.click();
        setTimeout(() => {
          window.open(messengerWebUrl, '_blank', 'noopener,noreferrer');
        }, 1500);
      } catch (error) {
        window.open(messengerWebUrl, '_blank', 'noopener,noreferrer');
      } finally {
        document.body.removeChild(appLink);
      }
    } else {
      window.open(`https://m.me/${pageId}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle favorite toggle
  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFavoriteLoading(true);
    
    try {
      await toggleFavorite(id);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Calculate discount information - now using product-level pricing
  const calculateDiscountInfo = useCallback(() => {
    if (!product) {
      setDiscountInfo({
        hasDiscount: false,
        discountPercent: 0,
        originalPrice: 0,
        discountedPrice: 0
      });
      return;
    }

    const price = product.price || 0;
    const newPrice = product.newPrice || product.new_price || null;
    
    if (newPrice && newPrice > 0 && newPrice < price) {
      const discountPercent = Math.round(((price - newPrice) / price) * 100);
      setDiscountInfo({
        hasDiscount: true,
        discountPercent,
        originalPrice: price,
        discountedPrice: newPrice
      });
    } else {
      setDiscountInfo({
        hasDiscount: false,
        discountPercent: 0,
        originalPrice: price,
        discountedPrice: price
      });
    }
  }, [product]);

  // Effect to recalculate discount when product changes
  useEffect(() => {
    calculateDiscountInfo();
  }, [calculateDiscountInfo]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // Fetch product details
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*, categories(name)')
          .eq('id', id)
          .single();
          
        if (productError) throw productError;
        
        // Fetch product colors with color info and images
        const { data: colorsData, error: colorsError } = await supabase
          .from('product_colors')
          .select(`
            *,
            colors(name),
            product_color_images(image_url, image_order)
          `)
          .eq('product_id', id)
          .order('color_order', { ascending: true });
          
        if (colorsError) throw colorsError;
        
        // Transform colors data to include ordered images
        const transformedColors = colorsData.map(color => ({
          ...color,
          images: color.product_color_images
            ? color.product_color_images
                .sort((a, b) => (a.image_order || 0) - (b.image_order || 0))
                .map(img => img.image_url)
            : []
        }));
        
        setProduct(productData);
        setProductColors(transformedColors);
        
        // Find home color or use first color
        if (transformedColors?.length > 0) {
          const homeColorIndex = transformedColors.findIndex(color => color.is_home_color);
          const initialColorIndex = homeColorIndex >= 0 ? homeColorIndex : 0;
          
          setSelectedColorIndex(initialColorIndex);
          await fetchColorDetails(transformedColors[initialColorIndex].id);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('حدث خطأ أثناء تحميل المنتج');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id]);
  
  // Fetch sizes for a selected color - now only fetches size and stock
  const fetchColorDetails = async (colorId) => {
    try {
      // Fetch sizes for this color (only size and stock, no price fields)
      const { data: sizesData, error: sizesError } = await supabase
        .from('product_color_sizes')
        .select('size, stock')
        .eq('product_color_id', colorId)
        .order('size');
        
      if (sizesError) throw sizesError;
      
      setAvailableSizes(sizesData);
      setSelectedSize('');
    } catch (err) {
      console.error('Error fetching color details:', err);
      setError('حدث خطأ أثناء تحميل تفاصيل المنتج');
    }
  };
  
  // Handle color change
  const handleColorChange = (index) => {
    setSelectedColorIndex(index);
    fetchColorDetails(productColors[index].id);
    setQuantity(1);
  };
  
  // Handle size change
  const handleSizeChange = (size) => {
    setSelectedSize(size);
    setError('');
    setQuantity(1);
  };

  const handleAddToCart = () => {
    if (availableSizes.length > 0 && !selectedSize) {
      setSizeDialogOpen(true);
      return;
    }

    const sizeData = availableSizes.find(s => s.size === selectedSize);
    
    if (!sizeData) {
      setError('المقاس غير متوفر');
      return;
    }
    
    if (quantity < 1) {
      setError('الكمية يجب أن تكون على الأقل 1');
      return;
    }

    if (quantity > sizeData.stock) {
      setError('الكمية المطلوبة تتجاوز المخزون المتاح');
      return;
    }

    // Add to cart with proper data - use product-level pricing
    const selectedColor = productColors[selectedColorIndex];
    const finalPrice = discountInfo.hasDiscount ? discountInfo.discountedPrice : discountInfo.originalPrice;
    
    const cartItem = {
      id: product.id,
      name_ar: product.name_ar,
      description_ar: product.description_ar,
      colorId: selectedColor.id,
      colorName: selectedColor.colors?.name || selectedColor.color,
      size: selectedSize,
      price: finalPrice,
      quantity: quantity,
      image: selectedColor.images?.[0] || '/placeholder-image.png'
    };
    
    addToCart(cartItem);
    
    // Show success dialog
    setSuccessDialogOpen(true);
  };

  // Handle add to cart from size dialog
  const handleAddToCartFromDialog = () => {
    handleAddToCart();
  };

  // Handle success dialog actions
  const handleViewCart = () => {
    setSuccessDialogOpen(false);
    navigate('/cart');
  };

  const handleContinueShopping = () => {
    setSuccessDialogOpen(false);
  };

  const handleShare = async () => {
    const shareData = {
      title: product.name_ar,
      text: product.description_ar,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareData.url);
        alert('تم نسخ رابط المنتج إلى الحافظة');
      } catch (err) {
        console.error('Could not copy link:', err);
      }
    } else {
      const dummy = document.createElement('input');
      document.body.appendChild(dummy);
      dummy.value = shareData.url;
      dummy.select();
      document.execCommand('copy');
      document.body.removeChild(dummy);
      alert('تم نسخ رابط المنتج إلى الحافظة');
    }
  };

  // Get display price - now uses product-level pricing
  const getDisplayPrice = () => {
    return discountInfo.hasDiscount ? discountInfo.discountedPrice : discountInfo.originalPrice;
  };

  // Get original price for display when there's a discount
  const getOriginalPrice = () => {
    return discountInfo.hasDiscount ? discountInfo.originalPrice : null;
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress size={60} />
    </Box>
  );
  
  if (!product) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h5">المنتج غير موجود</Typography>
    </Box>
  );

  // Get images for current color
  const currentColor = productColors[selectedColorIndex] || {};
  const imagesToShow = currentColor.images?.length > 0 
    ? currentColor.images 
    : ['/placeholder-image.png'];
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }} 
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ 
        maxWidth: 1200, 
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3
      }}>
        <ProductHeader 
          handleMessengerClick={handleMessengerClick}
          handleShare={handleShare}
          handleToggleFavorite={handleToggleFavorite}
          favoriteLoading={favoriteLoading}
          isFavorite={isFavorite}
          productId={id}
        />

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

        <Grid container spacing={4}>
          {/* Images Section */}
          <Grid item xs={12} md={6}>
            <ProductImageGallery 
              images={imagesToShow}
              currentImageIndex={currentImageIndex}
              setCurrentImageIndex={setCurrentImageIndex}
            />
          </Grid>

          {/* Product Details Section */}
          <Grid item xs={12} md={6}>
            <ProductDetails 
              product={product}
              productColors={productColors}
              selectedColorIndex={selectedColorIndex}
              handleColorChange={handleColorChange}
              availableSizes={availableSizes}
              selectedSize={selectedSize}
              handleSizeChange={handleSizeChange}
              quantity={quantity}
              setQuantity={setQuantity}
              handleAddToCart={handleAddToCart}
              error={error}
              displayPrice={getDisplayPrice()}
              originalPrice={getOriginalPrice()}
              hasDiscount={discountInfo.hasDiscount}
              discountPercent={discountInfo.discountPercent}
            />
          </Grid>
        </Grid>

        {/* Size Selection Dialog */}
        <SizeSelectionDialog
          open={sizeDialogOpen}
          onClose={() => setSizeDialogOpen(false)}
          availableSizes={availableSizes}
          selectedSize={selectedSize}
          onSizeChange={handleSizeChange}
          onAddToCart={handleAddToCartFromDialog}
        />

        {/* Success Dialog */}
        <SuccessDialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          productImage={imagesToShow[0]}
          productName={product.name_ar}
          onViewCart={handleViewCart}
          onContinueShopping={handleContinueShopping}
        />
      </Box>
    </motion.div>
  );
};

export default Product;
